// dashboard/frontend/src/pages/Contact.js
import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    TextField,
    Button,
    Box,
    Alert,
    Snackbar,
    Grid,
    useTheme,
    Divider
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import SupportIcon from '@mui/icons-material/Support';
import api from '../api/axios';

const Contact = () => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
    
        try {
            await api.post('/api/contact/', formData);
            setSuccess(true);
            setFormData({ name: '', email: '', company: '', message: '' });
        } catch (err) {
            console.error('Error:', err);
            setError(err.response?.data?.error || 'Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const contactInfo = [
        {
            icon: <EmailIcon fontSize="large" color="primary" />,
            title: "Email Us",
            description: "support@arguscms.com",
            action: "For general inquiries"
        },
        {
            icon: <BusinessIcon fontSize="large" color="primary" />,
            title: "Enterprise Solutions",
            description: "sales@arguscms.com",
            action: "For business opportunities"
        },
        {
            icon: <SupportIcon fontSize="large" color="primary" />,
            title: "Technical Support",
            description: "Available 24/7",
            action: "For existing customers"
        }
    ];

    return (
        <Box sx={{ 
            bgcolor: 'grey.50',
            minHeight: '100vh',
            py: 4
        }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Box
                    sx={{
                        textAlign: 'center',
                        mb: 6
                    }}
                >
                    <Typography 
                        variant="h2" 
                        gutterBottom
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.primary.main
                        }}
                    >
                        Get in Touch
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        We're here to help and answer any question you might have
                    </Typography>
                </Box>

                {/* Contact Info Cards */}
                <Grid container spacing={4} sx={{ mb: 6 }}>
                    {contactInfo.map((info, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    height: '100%',
                                    transition: '0.3s',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                {info.icon}
                                <Typography variant="h6" sx={{ mt: 2 }}>
                                    {info.title}
                                </Typography>
                                <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                                    {info.description}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {info.action}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Contact Form */}
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4,
                        maxWidth: 'md',
                        mx: 'auto',
                        bgcolor: 'white'
                    }}
                >
                    <Typography variant="h4" gutterBottom align="center">
                        Send us a Message
                    </Typography>
                    <Divider sx={{ mb: 4 }} />
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    name="message"
                                    multiline
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{ minWidth: 200 }}
                            >
                                {loading ? 'Sending...' : 'Send Message'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Container>

            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={() => setSuccess(false)}
            >
                <Alert severity="success">
                    Message sent successfully! We'll get back to you soon.
                </Alert>
            </Snackbar>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
            >
                <Alert severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contact;